import { Injectable, OnDestroy } from '@angular/core';
import { NavigationStart, Router, Event, ActivatedRouteSnapshot } from '@angular/router';
import { cloneDeep, merge } from 'lodash';
import { filter } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { LinkedPropertyUtility } from '@shared/utils/linked-property.utility';
import { ApiQueryOption } from '@shared/constants/api';
import { Title } from '@angular/platform-browser';
import { IBaseContext, IContext } from '@shared/interfaces/ai-chat.interface';
import { AI_CHAT_NAV_REGISTRY } from '../constants/ai-chat-nav-registry.constants';

@Injectable({ providedIn: 'root' })
export class ChatContextRegistry implements OnDestroy {
  // Context that is not tied to the current page and must survive navigation, unlike registeredContext/baseContext below.
  private readonly navigationContext: Record<string, unknown> = { navigation: AI_CHAT_NAV_REGISTRY };
  private baseContext: Record<string, unknown> = {};
  private registeredContext: Record<string, unknown> = {};
  private subscription: Subscription = new Subscription();
  private isDismissed: boolean = false;
  private _dismissed$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  shouldPassContext: boolean = true;
  readonly dismissed$: Observable<boolean> = this._dismissed$.asObservable();

  constructor(
    private router: Router,
    private titleService: Title,
  ) {
    this.subscription.add(
      this.router.events
        .pipe(filter((event: Event): event is NavigationStart => event instanceof NavigationStart))
        .subscribe((): void => {
          this.clear();
        }),
    );
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public getContext(): IContext {
    if (!this.shouldPassContext || this.isDismissed) {
      return {};
    }

    const base: IBaseContext = {
      ...this.getPageContext(),
      timezone: this.getTimeZone(),
      ...this.baseContext,
    };

    return { base, ...this.navigationContext, ...this.registeredContext };
  }

  public dismissContext(): void {
    this.isDismissed = true;
    this._dismissed$.next(true);
  }

  public restoreAllContext(): void {
    this.isDismissed = false;
    this._dismissed$.next(false);
  }

  /**
   * Registers or updates page-specific context that will be injected
   * into AI request generation.
   *
   * This context enables more relevant and accurate responses by providing
   * the AI layer with information about the current page or component state.
   * The context is stored in an internal registry and can be overwritten
   * or cleared when navigation changes.
   *
   * @param name - Unique context identifier (should use snake_case)
   * @param context - Serializable key-value object representing page state
   */
  public setContext(name: string, context: Record<string, unknown>): void {
    this.registeredContext[name] = cloneDeep(context);
  }

  public updateContext(name: string, context: Record<string, unknown>): void {
    this.registeredContext[name] = merge({}, this.registeredContext[name], context);
  }

  public setBaseContext(context: Record<string, unknown>): void {
    this.baseContext = cloneDeep(context);
  }

  public updateBaseContext(context: Record<string, unknown>): void {
    this.baseContext = merge({}, this.baseContext, context);
  }

  public clear(name?: string): void {
    if (!name) {
      this.registeredContext = {};
      this.baseContext = {};
    }

    delete this.registeredContext[name];
  }

  private getPageContext(): IBaseContext {
    const linkedPropertyData = this.getActiveRoute().queryParams[ApiQueryOption.linkedPropertyData];

    return {
      page: {
        url: this.router.url.split('?')[0],
        title: this.titleService.getTitle(),
      },
      ...LinkedPropertyUtility.fromLinkedPropertyString(linkedPropertyData ?? ''),
    };
  }

  private getTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  private getActiveRoute(): ActivatedRouteSnapshot {
    let route = this.router.routerState.snapshot.root;

    while (route.firstChild) {
      route = route.firstChild;
    }

    return route;
  }
}
